import { logger } from '@/configs'

/**
 * Gets the OneTrust instance from window object
 */
export function getOneTrust(): Window['OneTrust'] | null {
  return window.OneTrust || null
}

/**
 * Checks if OneTrust is available and ready
 */
export function isOneTrustReady(): boolean {
  const oneTrust = getOneTrust()
  // Check if OneTrust object exists and has basic methods
  // Also check if OnetrustActiveGroups is available as an alternative indicator
  const hasBasicMethods = !!(oneTrust && oneTrust.ToggleInfoDisplay)
  const hasActiveGroups = !!(
    window.OnetrustActiveGroups && Array.isArray(window.OnetrustActiveGroups)
  )

  return hasBasicMethods || hasActiveGroups
}

/**
 * Waits for OneTrust to be available and ready
 * @param callback Function to call when OneTrust is ready
 * @param maxAttempts Maximum number of attempts (default: 50)
 * @param interval Interval between attempts in milliseconds (default: 100)
 */
export function waitForOneTrust(
  callback: () => void,
  maxAttempts: number = 50,
  interval: number = 100,
): void {
  let attempts = 0

  function checkOneTrust() {
    attempts++

    if (isOneTrustReady()) {
      callback()
    } else if (attempts < maxAttempts) {
      setTimeout(checkOneTrust, interval)
    }
  }

  checkOneTrust()
}

/**
 * Checks OneTrust's initial consent state for a specific consent group
 * @param consentGroupId The consent group ID to check (e.g., 'C0002' for analytics)
 * @returns true if consent is granted, false otherwise
 */
export function checkConsentForGroup(consentGroupId: string): boolean {
  try {
    // Use window.OnetrustActiveGroups to check if consent group is active
    if (window.OnetrustActiveGroups) {
      // OnetrustActiveGroups can be either an array or a comma-separated string
      let hasConsent = false

      if (Array.isArray(window.OnetrustActiveGroups)) {
        hasConsent = window.OnetrustActiveGroups.includes(consentGroupId)
      } else if (typeof window.OnetrustActiveGroups === 'string') {
        // Handle comma-separated string format like ",C0001,C0002,C0003,C0004,"
        hasConsent = window.OnetrustActiveGroups.includes(consentGroupId)
      }
      return hasConsent
    }
  } catch (error) {
    logger.error('Error checking OneTrust consent: %o', error)
  }
  return false
}

/**
 * Sets up a OneTrust consent change listener for a specific consent group
 * @param consentGroupId The consent group ID to monitor (e.g., 'C0002' for analytics)
 * @param onConsentGranted Callback when consent is granted
 * @param onConsentRevoked Callback when consent is revoked
 * @param maxAttempts Maximum number of attempts to set up listener
 * @param interval Interval between attempts in milliseconds
 */
export function setupConsentListener(
  consentGroupId: string,
  onConsentGranted: () => void,
  onConsentRevoked: () => void,
  maxAttempts: number = 10,
  interval: number = 1000,
): void {
  let attempts = 0

  function setupListener() {
    attempts++
    const oneTrust = getOneTrust()

    if (oneTrust && oneTrust.OnConsentChanged) {
      oneTrust.OnConsentChanged(function (event: { detail: string[] }) {
        // Check if event.detail contains the specified consent group
        if (event.detail && Array.isArray(event.detail) && event.detail.includes(consentGroupId)) {
          onConsentGranted()
        } else {
          onConsentRevoked()
        }
      })
    } else if (attempts < maxAttempts) {
      // If OneTrust isn't ready yet, wait and try again
      setTimeout(setupListener, interval)
    } else {
      logger.warn(
        'Failed to set up OneTrust listener for group %s after maximum attempts',
        consentGroupId,
      )
    }
  }

  setupListener()
}
